import { useEffect, useRef, useState, type ReactNode } from "react";

import { fetchEngineEventsPage } from "./engine-progress/engineEvents";

type UnknownRecord = Record<string, unknown>;

const UNRESOLVED_JSON_REF = Symbol("unresolved-json-ref");

export interface RequirementsStageInput {
  readonly artifact?: unknown;
  readonly resolvedArtifact?: unknown;
  readonly status?: unknown;
  readonly error?: unknown;
  readonly requirementsArtifactPath?: unknown;
  readonly sidecars?: Readonly<Record<string, unknown>>;
  readonly artifactSidecars?: Readonly<Record<string, unknown>>;
  readonly resolveJsonRef?: (ref: Readonly<Record<string, unknown>>) => unknown;
}

export interface EngineRequirementRow {
  readonly id: string;
  readonly statement: string;
  readonly anchorText?: string;
  readonly sourceDocId?: string;
  readonly sourceDocumentLabel?: string;
  readonly sourcePage?: string | number;
}

export type RequirementsStageStatus =
  | "not-run"
  | "running"
  | "failed"
  | "draft"
  | "complete"
  | "unavailable";

export interface NormalizedRequirementsStage {
  readonly rows: readonly EngineRequirementRow[];
  readonly status: RequirementsStageStatus;
  readonly notice: string | null;
  readonly artifactAvailable: boolean;
}

export interface RequirementsSurfaceProps {
  readonly requirementsStage?: RequirementsStageInput | null | undefined;
  readonly requirementsArtifact?: unknown;
  readonly requirementsArtifactPath?: string | null | undefined;
}

const asRecord = (value: unknown): UnknownRecord | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;

const nonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const scalarLabel = (value: unknown): string | number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return nonEmptyString(value);
};

const firstNonEmptyString = (...values: readonly unknown[]): string | undefined => {
  for (const value of values) {
    const result = nonEmptyString(value);

    if (result) {
      return result;
    }
  }

  return undefined;
};

const requirementsPath = (record: UnknownRecord): string | undefined =>
  firstNonEmptyString(
    record.requirementsArtifactPath,
    record.requirements_artifact_path
  );

const artifactRefPath = (record: UnknownRecord): string | undefined => {
  const refs = record.artifactRefs;

  if (!Array.isArray(refs)) {
    return undefined;
  }

  return refs.reduce<string | undefined>(
    (current, ref) => current ?? firstNonEmptyString(asRecord(ref)?.path),
    undefined
  );
};

const isRequirementsArtifact = (record: UnknownRecord): boolean =>
  ["aggregation", "output", "aggregatedRequirements", "lineage"].some(
    (key) => record[key] !== undefined
  );

const requirementsSurfaceForCandidate = (
  candidate: unknown
): RequirementsSurfaceProps | null => {
  if (candidate === undefined || candidate === null) {
    return null;
  }

  const record = asRecord(candidate);
  const isStageEnvelope =
    record !== null &&
    [
      "artifact",
      "resolvedArtifact",
      "requirementsArtifact",
      "result",
      "requirementsArtifactPath",
      "requirements_artifact_path",
      "sidecars",
      "artifactSidecars",
      "resolveJsonRef"
    ].some((key) => record[key] !== undefined);

  return isStageEnvelope
    ? { requirementsStage: candidate as RequirementsStageInput }
    : { requirementsArtifact: candidate };
};

/**
 * Extract the requirements result without treating unrelated engine outputs
 * as requirements. The transport may nest the stage under engineResults,
 * result, or an event payload, and the stage may contain only its stable path.
 */
export function requirementsSurfaceFromEngineResults(
  value: unknown
): RequirementsSurfaceProps | null {
  const pending: unknown[] = [value];
  const seen = new Set<object>();

  while (pending.length > 0) {
    const current = pending.shift();

    if (Array.isArray(current)) {
      pending.push(...current);
      continue;
    }

    const record = asRecord(current);

    if (!record) {
      continue;
    }

    if (seen.has(record)) {
      continue;
    }

    seen.add(record);

    const stageId = nonEmptyString(record.stageId);
    const eventPayload = asRecord(record.payload);

    if (stageId && /requirement/iu.test(stageId) && eventPayload) {
      const eventCandidate = eventPayload.engineResult;
      const eventSurface = eventCandidate
        ? requirementsSurfaceFromEngineResults(eventCandidate)
        : null;

      if (eventSurface) {
        return eventSurface;
      }

      const eventPath = requirementsPath(eventPayload) ?? artifactRefPath(eventPayload);

      if (eventPath) {
        return { requirementsStage: { requirementsArtifactPath: eventPath } };
      }
    }

    for (const key of [
      "requirementsStage",
      "requirements",
      "requirementsResult",
      "requirementAggregation"
    ]) {
      if (record[key] !== undefined) {
        const surface = requirementsSurfaceForCandidate(record[key]);

        if (surface) {
          return surface;
        }
      }
    }

    if (record.requirementsArtifact !== undefined) {
      const surface = requirementsSurfaceForCandidate(record.requirementsArtifact);

      if (surface) {
        return surface;
      }
    }

    const path = requirementsPath(record);

    if (path) {
      const sidecars = asRecord(record.sidecars);
      const artifactSidecars = asRecord(record.artifactSidecars);

      return {
        requirementsStage: {
          ...(record.artifact !== undefined ? { artifact: record.artifact } : {}),
          ...(record.resolvedArtifact !== undefined
            ? { resolvedArtifact: record.resolvedArtifact }
            : {}),
          ...(sidecars ? { sidecars } : {}),
          ...(artifactSidecars ? { artifactSidecars } : {}),
          ...(record.status !== undefined ? { status: record.status } : {}),
          ...(record.error !== undefined ? { error: record.error } : {}),
          ...(record.failure !== undefined ? { failure: record.failure } : {}),
          requirementsArtifactPath: path
        }
      };
    }

    if (isRequirementsArtifact(record)) {
      return { requirementsArtifact: current };
    }

    for (const key of [
      "engineResults",
      "specResult",
      "specResults",
      "result",
      "payload",
      "engineResult",
      "requirementsOutput",
      "outputs",
      "stages",
      "events",
      "data"
    ]) {
      if (record[key] !== undefined) {
        pending.push(record[key]);
      }
    }
  }

  return null;
}

/** Read the retained requirements result for a durable engine run. */
export function useRequirementsStageForRun(
  runId: string | null | undefined
): RequirementsSurfaceProps {
  const [surface, setSurface] = useState<RequirementsSurfaceProps>({});
  const cursorRef = useRef(-1);

  useEffect(() => {
    cursorRef.current = -1;
    setSurface({});

    if (!runId) {
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const page = await fetchEngineEventsPage(runId, cursorRef.current);

        if (page.events.length > 0) {
          cursorRef.current = page.events.at(-1)?.seq ?? cursorRef.current;
        }

        const next = requirementsSurfaceFromEngineResults({
          engineResults: page.engineResults,
          events: page.events
        });

        if (!cancelled && next) {
          setSurface(next);
        }
      } catch {
        // Keep the last verified result. The consuming surfaces render their
        // honest absence state when no retained result has arrived yet.
      }
    };

    void poll();
    const intervalId = globalThis.setInterval(() => void poll(), 900);

    return () => {
      cancelled = true;
      globalThis.clearInterval(intervalId);
    };
  }, [runId]);

  return surface;
}

const readPath = (value: unknown, path: readonly string[]): unknown => {
  let current: unknown = value;

  for (const key of path) {
    const record = asRecord(current);

    if (!record) {
      return undefined;
    }

    current = record[key];
  }

  return current;
};

const isJsonRef = (value: unknown): value is UnknownRecord => {
  const record = asRecord(value);

  return (
    record !== null &&
    (record.__requirementsV1Type === "JsonRef" ||
      record.type === "JsonRef" ||
      record.type === "json_ref")
  );
};

const sidecarCandidates = (ref: UnknownRecord): string[] => {
  const path = nonEmptyString(ref.path);
  const sha256 = nonEmptyString(ref.sha256);
  const pathName = path?.split(/[\\/]/u).at(-1);
  return [
    ...new Set([path, pathName, sha256].filter((value): value is string => !!value))
  ];
};

const resolveSidecarValue = (
  ref: UnknownRecord,
  sidecars: Readonly<Record<string, unknown>> | undefined,
  resolveJsonRef: RequirementsStageInput["resolveJsonRef"],
  seen: ReadonlySet<string>
): unknown | typeof UNRESOLVED_JSON_REF => {
  const refKey = sidecarCandidates(ref).join("|");

  if (seen.has(refKey)) {
    return UNRESOLVED_JSON_REF;
  }

  let found = false;
  let replacement: unknown;

  if (resolveJsonRef) {
    try {
      replacement = resolveJsonRef(ref);
      found = replacement !== undefined;
    } catch {
      found = false;
    }
  }

  if (!found && sidecars) {
    for (const candidate of sidecarCandidates(ref)) {
      if (Object.prototype.hasOwnProperty.call(sidecars, candidate)) {
        replacement = sidecars[candidate];
        found = true;
        break;
      }
    }
  }

  if (!found) {
    return UNRESOLVED_JSON_REF;
  }

  return resolveJsonRefs(
    replacement,
    sidecars,
    resolveJsonRef,
    new Set([...seen, refKey])
  );
};

const resolveJsonRefs = (
  value: unknown,
  sidecars: Readonly<Record<string, unknown>> | undefined,
  resolveJsonRef: RequirementsStageInput["resolveJsonRef"],
  seen: ReadonlySet<string> = new Set()
): unknown | typeof UNRESOLVED_JSON_REF => {
  if (isJsonRef(value)) {
    return resolveSidecarValue(value, sidecars, resolveJsonRef, seen);
  }

  if (Array.isArray(value)) {
    const resolved: unknown[] = [];

    for (const item of value) {
      const next = resolveJsonRefs(item, sidecars, resolveJsonRef, seen);

      if (next === UNRESOLVED_JSON_REF) {
        return UNRESOLVED_JSON_REF;
      }

      resolved.push(next);
    }

    return resolved;
  }

  const record = asRecord(value);

  if (!record) {
    return value;
  }

  const resolved: UnknownRecord = {};

  for (const [key, item] of Object.entries(record)) {
    const next = resolveJsonRefs(item, sidecars, resolveJsonRef, seen);

    if (next === UNRESOLVED_JSON_REF) {
      return UNRESOLVED_JSON_REF;
    }

    resolved[key] = next;
  }

  return resolved;
};

const stageRecord = (stage: unknown): UnknownRecord | null => asRecord(stage);

const unwrapArtifact = (value: unknown, depth = 0): unknown => {
  if (depth > 4 || isJsonRef(value)) {
    return value;
  }

  const record = stageRecord(value);

  if (!record) {
    return value;
  }

  for (const key of [
    "resolvedArtifact",
    "artifact",
    "requirementsArtifact",
    "result"
  ]) {
    if (record[key] !== undefined) {
      return unwrapArtifact(record[key], depth + 1);
    }
  }

  if (
    ["aggregation", "output", "aggregatedRequirements", "lineage"].some(
      (key) => record[key] !== undefined
    )
  ) {
    return value;
  }

  return undefined;
};

const stageArtifact = (
  stage: unknown,
  directArtifact: unknown
): unknown => {
  const unwrapped = unwrapArtifact(
    directArtifact !== undefined ? directArtifact : stage
  );

  if (unwrapped !== undefined) {
    return unwrapped;
  }

  const path = nonEmptyString(stageRecord(stage)?.requirementsArtifactPath);

  return path ? { type: "JsonRef", path } : undefined;
};

const stageStatus = (stage: unknown, artifact: unknown): string | undefined => {
  const stageValue = stageRecord(stage);
  const artifactValue = asRecord(artifact);
  const resultValue = asRecord(stageValue?.result);

  return (
    firstNonEmptyString(
      stageValue?.status,
      stageValue?.stageStatus,
      stageValue?.state,
      resultValue?.status,
      artifactValue?.status,
      artifactValue?.stageStatus,
      artifactValue?.state
    )?.toLowerCase() ??
    (nonEmptyString(stageValue?.requirementsArtifactPath) ? "unavailable" : undefined)
  );
};

const hasError = (stage: unknown, artifact: unknown): boolean => {
  const stageValue = stageRecord(stage);
  const artifactValue = asRecord(artifact);

  return (
    stageValue?.error !== undefined ||
    stageValue?.failure !== undefined ||
    artifactValue?.error !== undefined ||
    artifactValue?.failure !== undefined
  );
};

const aggregatedRequirements = (artifact: unknown): readonly unknown[] | undefined => {
  const candidates = [
    readPath(artifact, ["aggregation", "output", "aggregatedRequirements"]),
    readPath(artifact, ["aggregation", "aggregatedRequirements"]),
    readPath(artifact, ["output", "aggregatedRequirements"]),
    readPath(artifact, ["aggregatedRequirements"])
  ];

  return candidates.find((candidate): candidate is readonly unknown[] =>
    Array.isArray(candidate)
  );
};

const lineageEntries = (artifact: unknown): readonly UnknownRecord[] => {
  const candidates = [
    readPath(artifact, ["aggregation", "output", "lineage"]),
    readPath(artifact, ["aggregation", "lineage"]),
    readPath(artifact, ["output", "lineage"]),
    readPath(artifact, ["lineage"])
  ];
  const lineage = candidates.find(Array.isArray);

  return Array.isArray(lineage)
    ? lineage.map(asRecord).filter((value): value is UnknownRecord => value !== null)
    : [];
};

const requirementId = (value: UnknownRecord): string | undefined =>
  firstNonEmptyString(
    value.requirementId,
    value.id,
    value.key,
    asRecord(value.requirement)?.requirementId,
    asRecord(value.requirement)?.id,
    asRecord(value.refinedRequirement)?.requirementId,
    asRecord(value.refinedRequirement)?.id
  );

/**
 * Match a row to its lineage entries by requirement id ONLY. The engine
 * contract (aggregatedRequirementSchema, engines/requirements/aggregation.ts)
 * requires every aggregated requirement to carry a non-empty requirementId,
 * and lineage entries are built one-to-one from those same requirement
 * objects (engines/requirements/v1-runner.ts, resolveAggregatedRequirementLineage),
 * so a real lineage entry always carries the id that produced it. There is
 * no legitimate id-less lineage shape on the production path, so there is no
 * safe positional fallback: attaching lineage[index] when the id does not
 * match would attach a DIFFERENT requirement's source anchor and document,
 * fabricating provenance. An unmatched row correctly gets no lineage, and
 * the UI degrades honestly ("Source anchor unavailable").
 */
const relatedLineage = (
  row: UnknownRecord,
  lineage: readonly UnknownRecord[]
): readonly UnknownRecord[] => {
  const id = requirementId(row);

  if (!id) {
    return [];
  }

  return lineage.filter((entry) => {
    const entryRequirement = asRecord(entry.requirement);
    return [
      requirementId(entry),
      entryRequirement ? requirementId(entryRequirement) : undefined
    ].includes(id);
  });
};

const pushRecord = (target: UnknownRecord[], value: unknown): void => {
  const record = asRecord(value);

  if (record) {
    target.push(record);
  }
};

const evidenceRecords = (
  row: UnknownRecord,
  lineage: readonly UnknownRecord[]
): readonly UnknownRecord[] => {
  const records: UnknownRecord[] = [];
  const addEvidenceTree = (value: unknown): void => {
    const record = asRecord(value);

    if (!record) {
      return;
    }

    records.push(record);
    pushRecord(records, record.anchorVerification);
    pushRecord(records, record.sourceAnchor);
    pushRecord(records, record.anchor);
    pushRecord(records, record.source);

    for (const key of ["pulls", "sources", "documents", "evidence", "refinements"]) {
      const values = record[key];

      if (Array.isArray(values)) {
        for (const item of values) {
          addEvidenceTree(item);
        }
      }
    }
  };

  addEvidenceTree(row);
  for (const entry of relatedLineage(row, lineage)) {
    addEvidenceTree(entry);
  }

  return records;
};

const resolvedAnchorText = (records: readonly UnknownRecord[]): string | undefined => {
  for (const record of records) {
    const anchorCandidates = [
      record.anchorVerification,
      record.sourceAnchor,
      record.anchor,
      record
    ];

    for (const candidate of anchorCandidates) {
      const anchor = asRecord(candidate);
      const text = firstNonEmptyString(anchor?.resolvedText, anchor?.resolved_text);

      if (text) {
        return text;
      }
    }
  }

  return undefined;
};

const sourceRecords = (records: readonly UnknownRecord[]): readonly UnknownRecord[] => {
  const sources: UnknownRecord[] = [];

  for (const record of records) {
    pushRecord(sources, record);
    pushRecord(sources, record.sourceDocument);
    pushRecord(sources, record.document);
    pushRecord(sources, record.source);
    pushRecord(sources, record.documentRef);

    for (const key of ["documents", "sources"]) {
      const values = record[key];

      if (Array.isArray(values)) {
        for (const value of values) {
          pushRecord(sources, value);
          const nested = asRecord(value);
          pushRecord(sources, nested?.document);
          pushRecord(sources, nested?.sourceDocument);
        }
      }
    }
  }

  return sources;
};

const sourceDocId = (records: readonly UnknownRecord[]): string | undefined => {
  for (const record of sourceRecords(records)) {
    const id = firstNonEmptyString(
      record.docId,
      record.documentId,
      record.document_id,
      record.sourceDocId,
      record.sourceDocumentId
    );

    if (id) {
      return id;
    }
  }

  return undefined;
};

const sourceDocumentLabel = (records: readonly UnknownRecord[]): string | undefined => {
  for (const record of sourceRecords(records)) {
    const label = firstNonEmptyString(
      record.title,
      record.name,
      record.filename,
      record.path
    );

    if (label) {
      return label;
    }
  }

  return undefined;
};

const sourcePage = (records: readonly UnknownRecord[]): string | number | undefined => {
  for (const record of sourceRecords(records)) {
    const page = scalarLabel(
      record.page ??
        record.pageNumber ??
        record.page_number ??
        record.sourcePageNumber ??
        record.source_page_number ??
        record.pageRef
    );

    if (page !== undefined) {
      return page;
    }
  }

  return undefined;
};

const rowStatement = (row: UnknownRecord): string | undefined =>
  firstNonEmptyString(
    row.statement,
    row.requirement,
    row.text,
    asRecord(row.refinedRequirement)?.statement,
    asRecord(row.requirement)?.statement
  );

const normalizeRows = (
  rawRows: readonly unknown[],
  artifact: unknown
): readonly EngineRequirementRow[] => {
  const lineage = lineageEntries(artifact);

  return rawRows.flatMap((rawRow, index) => {
    const row = asRecord(rawRow);

    if (!row) {
      return [];
    }

    const statement = rowStatement(row);

    if (!statement) {
      return [];
    }

    const evidence = evidenceRecords(row, lineage);
    const anchorText = resolvedAnchorText(evidence);
    const docId = sourceDocId(evidence);
    const documentLabel = sourceDocumentLabel(evidence);
    const page = sourcePage(evidence);

    return [
      {
        id: requirementId(row) ?? `requirement-${index + 1}`,
        statement,
        ...(anchorText ? { anchorText } : {}),
        ...(docId ? { sourceDocId: docId } : {}),
        ...(documentLabel ? { sourceDocumentLabel: documentLabel } : {}),
        ...(page !== undefined ? { sourcePage: page } : {})
      }
    ];
  });
};

const statusFor = (
  rawStatus: string | undefined,
  stage: unknown,
  artifact: unknown,
  artifactAvailable: boolean,
  hasRows: boolean
): RequirementsStageStatus => {
  if (
    hasError(stage, artifact) ||
    /fail|error|cancel|stopped/iu.test(rawStatus ?? "")
  ) {
    return "failed";
  }

  if (/^unavailable$/iu.test(rawStatus ?? "")) {
    return "unavailable";
  }

  if (/^(?:not[-_ ]?(?:started|run)|idle)$/iu.test(rawStatus ?? "")) {
    return artifactAvailable ? "complete" : "not-run";
  }

  if (/run|queue|process|pending|start/iu.test(rawStatus ?? "")) {
    return "running";
  }

  if (/draft|minimal|partial|incomplete|provisional/iu.test(rawStatus ?? "")) {
    return "draft";
  }

  if (!artifactAvailable) {
    return rawStatus ? "unavailable" : "not-run";
  }

  return "complete";
};

const noticeFor = (
  status: RequirementsStageStatus,
  artifactAvailable: boolean,
  hasAggregatedRequirements: boolean,
  rowCount: number
): string | null => {
  if (status === "failed") {
    return "Requirements stage failed; no requirements are available.";
  }

  if (status === "running") {
    return "Requirements stage is running; no requirements are available yet.";
  }

  if (status === "draft") {
    return "Draft — the requirements stage returned partial results.";
  }

  if (status === "not-run") {
    return "Requirements stage hasn't run yet.";
  }

  if (!artifactAvailable) {
    return "Requirements artifact is unavailable.";
  }

  if (!hasAggregatedRequirements) {
    return "Requirements artifact has no readable aggregated requirements.";
  }

  return rowCount === 0 ? "The requirements stage returned no requirements." : null;
};

export function normalizeRequirementsStage({
  requirementsStage,
  requirementsArtifact,
  requirementsArtifactPath
}: RequirementsSurfaceProps): NormalizedRequirementsStage {
  const stage =
    requirementsStage ??
    (requirementsArtifactPath !== undefined ? { requirementsArtifactPath } : undefined);
  const stageValue = stageRecord(stage);
  const sidecars =
    asRecord(stageValue?.sidecars ?? stageValue?.artifactSidecars) ?? undefined;
  const resolveJsonRef =
    typeof stageValue?.resolveJsonRef === "function"
      ? (stageValue.resolveJsonRef as unknown as RequirementsStageInput["resolveJsonRef"])
      : undefined;
  const candidate = stageArtifact(stage, requirementsArtifact);
  const resolved = resolveJsonRefs(candidate, sidecars, resolveJsonRef);
  const artifactAvailable = resolved !== undefined && resolved !== UNRESOLVED_JSON_REF;
  const artifact = artifactAvailable ? resolved : undefined;
  const rawRows = aggregatedRequirements(artifact);
  const rows = rawRows ? normalizeRows(rawRows, artifact) : [];
  const status = statusFor(
    stageStatus(stage, artifact),
    stage,
    artifact,
    artifactAvailable,
    rows.length > 0
  );

  return {
    rows,
    status,
    notice: noticeFor(status, artifactAvailable, rawRows !== undefined, rows.length),
    artifactAvailable
  };
}

export function filterRequirementsForDocument(
  rows: readonly EngineRequirementRow[],
  documentId: string | null | undefined
): readonly EngineRequirementRow[] {
  if (!documentId) {
    return rows;
  }

  return rows.filter((row) => row.sourceDocId === documentId);
}

function RequirementsNoticeText({
  children,
  className = ""
}: {
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <p
      data-testid="requirements-stage-notice"
      className={`text-[12px] font-semibold leading-[1.5] text-[var(--muted)] ${className}`}
    >
      {children}
    </p>
  );
}

export function RequirementsStageNotice({
  requirementsStage,
  requirementsArtifact,
  requirementsArtifactPath,
  className
}: RequirementsSurfaceProps & { readonly className?: string }) {
  const model = normalizeRequirementsStage({
    requirementsStage,
    requirementsArtifact,
    requirementsArtifactPath
  });

  return model.notice ? (
    <RequirementsNoticeText className={className}>
      {model.notice}
    </RequirementsNoticeText>
  ) : null;
}

export function EngineRequirementsList({
  rows,
  variant = "step"
}: {
  readonly rows: readonly EngineRequirementRow[];
  readonly variant?: "step" | "rail";
}) {
  return (
    <ol
      className="flex flex-col gap-2"
      data-testid="requirements-engine-list"
      data-requirements-variant={variant}
    >
      {rows.map((row, index) => (
        <li
          key={`${row.id}-${index}`}
          data-testid="requirements-engine-row"
          className="rounded-[10px] border border-[var(--line3)] bg-[var(--surface)] p-3"
        >
          <p className="m-0 text-[12.5px] font-semibold leading-[1.45] text-[var(--ink)]">
            {row.statement}
          </p>
          {row.anchorText ? (
            <blockquote
              data-testid="requirements-engine-anchor"
              className="m-0 mt-2 border-l-2 border-[var(--line3)] pl-2.5 text-[11.5px] font-semibold leading-[1.45] text-[var(--muted)]"
            >
              {row.anchorText}
            </blockquote>
          ) : (
            <p
              data-testid="requirements-engine-anchor-missing"
              className="m-0 mt-2 text-[11px] font-semibold text-[var(--faint)]"
            >
              Source anchor unavailable.
            </p>
          )}
          {row.sourceDocId ||
          row.sourceDocumentLabel ||
          row.sourcePage !== undefined ? (
            <p
              data-testid="requirements-engine-source"
              className="m-0 mt-2 text-[10.5px] font-bold text-[var(--faint)]"
            >
              Source:{" "}
              {[row.sourceDocId, row.sourceDocumentLabel].filter(Boolean).join(" · ") ||
                "document unavailable"}
              {row.sourcePage !== undefined ? ` · page ${row.sourcePage}` : ""}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export function RequirementsArtifactPanel({
  requirementsStage,
  requirementsArtifact,
  requirementsArtifactPath,
  documentId,
  variant = "step"
}: RequirementsSurfaceProps & {
  readonly documentId?: string | null;
  readonly variant?: "step" | "rail";
}) {
  const model = normalizeRequirementsStage({
    requirementsStage,
    requirementsArtifact,
    requirementsArtifactPath
  });
  const rows = filterRequirementsForDocument(model.rows, documentId);
  const filteredOut = model.rows.length > 0 && rows.length === 0;

  return (
    <div data-testid="requirements-engine-panel">
      {model.notice ? (
        <RequirementsNoticeText>{model.notice}</RequirementsNoticeText>
      ) : null}
      {rows.length > 0 ? (
        <EngineRequirementsList rows={rows} variant={variant} />
      ) : null}
      {!model.notice && filteredOut ? (
        <RequirementsNoticeText>
          No requirements are linked to this document.
        </RequirementsNoticeText>
      ) : null}
    </div>
  );
}
